import {
    addSortToUrl,
    createUploadFormData,
    processFilters,
    processGetManyByReferenceFilter,
    processGetManyFilter, readJsonOrRefuse, refuseOnHttpError} from "./provider/defaults";
import {
    CreateParams,
    CreateResult,
    DataProvider,
    DeleteManyParams,
    DeleteParams,
    DeleteResult,
    GetListParams,
    GetListResult,
    GetManyParams,
    GetManyReferenceParams,
    GetManyReferenceResult,
    GetManyResult,
    GetOneParams,
    GetOneResult,
    UpdateManyParams,
    UpdateManyResult,
    UpdateParams,
    UpdateResult
} from "react-admin";
import {read_cookie} from 'sfcookies';
import {apiUrl, hostUrl, UseUrlManagerCleanUrl} from "../components/utils/url-manager";
import { NokWebsite } from "../types/entities";
import { addUrlToUri, removeUrlFromUri, restoreEntityString, cleanEntityString, convertFileToBase64 } from "./provider/defaults";



function cleanParams(data: any[]) : NokWebsite {
    let industries = data['targetIndustries'];
    if (industries?.length > 0) {
        industries = industries.split(',')
    }

    let imageUrl = null;
    if (data['logo'] !== undefined && data['logo'].length > 0) {
        imageUrl = hostUrl + data['logo'];
    }

    return {
        id: data['id'],
        name: data['name'],
        description: data['description'],
        senderDescription: data['senderDescription'] ?? null,
        targetIndustries: industries,
        industries: (data['industries'] ?? []).map( (iri: string): number => Number(String(iri).replace('/hq_api/industries/', '')) ),
        soloOrCompany: data['soloOrCompany'],
        nokPersonas: removeUrlFromUri(data['nokPersonas'], '/hq_api/nok_personas/'),
        url: data['url'],
        logo: {
            src: imageUrl,
            title: data['name'] + ' Logo'
        },
        productionLogoPath: data['productionLogoPath'],
        status: data['status'],
        nokEmailSignature: cleanEntityString(data['nokEmailSignature'], '/hq_api/nok_email_signatures/'),
        smtpServerZone: data['smtpServerZone'],
        nokEmailTemplate: cleanEntityString(data['nokEmailTemplate'], '/hq_api/nok_email_templates/'),
    }
}

// The website's own industries go to HQ-API as IRIs. An empty list clears them.
function industryIris(ids: number[] | undefined): string[] | undefined {
    return ids?.map( (id: number): string => '/hq_api/industries/' + id );
}

const nokwebsiteDataProvider: DataProvider = {

    getList: async (resource: string, params: GetListParams) : Promise<GetListResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += '?page=' + params.pagination.page + '&itemsPerPage=' + params.pagination.perPage;
        }
        url = processFilters(url, params);
        url = addSortToUrl(url, params);
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any) => {
                let responseData: { data: NokWebsite[], total: number } = {
                    data: [],
                    total: 0
                };
                // console.log(data);
                if (Array.isArray(data['hydra:member'])) {
                    let websites: any = data['hydra:member'];
                    let totalItems: number = data['hydra:totalItems'];

                    for(let i: number = 0; i < websites.length; i++) {
                        let website = websites[i];
                        responseData.data.push(cleanParams(website))
                    }
                    responseData.total = totalItems;
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error)
            });
        //return Promise.reject();
    },
    // get a single record by id
    getOne: async (resource: string, params: GetOneParams) : Promise<GetOneResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource + '/' + params.id;
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any): { data: NokWebsite } => {
                // console.log(data);
                let responseData: { data: NokWebsite } = {
                    data: undefined
                };
                if (data['id']) {
                    responseData.data = cleanParams(data);
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error);
            });
    },
    // get a list of records based on an array of ids
    getMany: async (resource: string, params: GetManyParams): Promise<GetManyResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        url = processGetManyFilter(url, params);
        url = addSortToUrl(url, params);
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any) => {
                let responseData: { data: NokWebsite[], total: number } = {
                    data: [],
                    total: 0
                };

                if (Array.isArray(data['hydra:member'])) {
                    let operators: any[] = data['hydra:member'];
                    let totalItems: number = data['hydra:totalItems'];

                    for(let i: number = 0; i < operators.length; i++) {
                        let operator: any[] = operators[i];
                        responseData.data.push(cleanParams(operator));
                    }
                    responseData.total = totalItems;
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error)
            });
    },
    // get the records referenced to another record, e.g. comments for a post
    getManyReference: async (resource: string, params: GetManyReferenceParams) : Promise<GetManyReferenceResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += '?page=' + params.pagination.page + '&itemsPerPage=' + params.pagination.perPage;
        }
        url = processGetManyByReferenceFilter(url, params);
        url = addSortToUrl(url, params);
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any) => {
                let responseData: { data: NokWebsite[], total: number } = {
                    data: [],
                    total: 0
                };

                if (Array.isArray(data['hydra:member'])) {
                    let operators: any[] = data['hydra:member'];
                    let totalItems: number = data['hydra:totalItems'];

                    for(let i: number = 0; i < operators.length; i++) {
                        let operator: any[] = operators[i];
                        responseData.data.push(cleanParams(operator));
                    }
                    responseData.total = totalItems;
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error)
            });
    },
    // create a record
    create: async (resource: string, params: CreateParams) : Promise<CreateResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        url = UseUrlManagerCleanUrl({ url: url });

        const today = new Date();
        let dateString = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate();
        params.data.updatedAt = dateString;
        params.data.nokEmailSignature = restoreEntityString(params.data.nokEmailSignature, '/hq_api/nok_email_signatures/');
        params.data.nokEmailTemplate = restoreEntityString(params.data.nokEmailTemplate, '/hq_api/nok_email_templates/');
        params.data.targetIndustries = params.data.targetIndustries?.join(',');
        params.data.industries = industryIris(params.data.industries);
        params.data.nokPersonas = addUrlToUri(params.data.nokPersonas, '/hq_api/nok_personas/')
        try {
            if (params.data.logo?.rawFile !== undefined) {
                const uploadData: { video?: string, image?: string, error?:string } = await createUploadFormData(
                    params, { image: 'logo', video: 'video' },
                    'nok_websites'
                );
                if (uploadData.error !== undefined) {
                    throw Error(uploadData.error);
                }
                params.data.logo = uploadData?.image ?? undefined;
            }

        } catch (error) {
            // Do nothing as no image to convert
        }

        const request: Request = new Request(url, {
            method: 'POST',
            body: JSON.stringify(params['data']),
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then(data => {
                let responseData: { data: NokWebsite } = {
                    data: undefined
                };
                if (data['id']) {
                    responseData.data = cleanParams(data);
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error);
            });

    },
    // update a record based on a patch
    update: async (resource: string, params: UpdateParams) : Promise<UpdateResult> => {
         let token: string = read_cookie('auth');
         let url: string = apiUrl + resource + '/' + params.data.id;
         url = UseUrlManagerCleanUrl({ url: url });
        const today = new Date();
        params.data.updatedAt = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate();
        params.data.targetIndustries = params.data.targetIndustries?.join(',');
        params.data.industries = industryIris(params.data.industries);
        params.data.nokEmailSignature = restoreEntityString(params.data.nokEmailSignature, '/hq_api/nok_email_signatures/');
        params.data.nokEmailTemplate = restoreEntityString(params.data.nokEmailTemplate, '/hq_api/nok_email_templates/');
        params.data.nokPersonas = addUrlToUri(params.data.nokPersonas, '/hq_api/nok_personas/')
        try {
            if (params.data.logo?.rawFile !== undefined) {
                const uploadData: { video?: string, image?: string, error?:string } = await createUploadFormData(
                    params, { image: 'logo', video: 'video' },
                    'nok_websites'
                );
                if (uploadData.error !== undefined) {
                    throw Error(uploadData.error);
                }
                params.data.logo = uploadData?.image ?? undefined;
            } else {
                params.data.logo = params.data.logo?.src.replace(hostUrl, '') ?? undefined;
            }
        } catch (error) {
            // Do nothing as no image to convert
        }


        const request: Request = new Request(url, {
            method: 'PATCH',
            body: JSON.stringify(params['data']),
            headers: new Headers({
                'Content-Type' : 'application/merge-patch+json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any) => {
                let responseData: { data: NokWebsite } = {
                    data: undefined
                };
                if (data['id']) {
                    responseData.data = cleanParams(data);
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error);
            });
    },
    // update a list of records based on an array of ids and a common patch
    updateMany: async (resource: string, params: UpdateManyParams) : Promise<UpdateManyResult> => {
        return Promise.reject();
    },
    // delete a record by id
    delete: async (resource: string, params: DeleteParams) : Promise<DeleteResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource + '/' + params.id;
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'DELETE',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return refuseOnHttpError(response)
                    .then((): { data: any } => ({ data: params }));
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error);
            });
    },
    // delete a list of records based on an array of ids
    deleteMany: async (resource: string, params: DeleteManyParams) : Promise<any> => {
        return Promise.reject();
    },
    getProfileData: (params) => {
        return Promise.resolve();
    }
}


export default nokwebsiteDataProvider;